import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";

import { crmService } from "@/services/crmService";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RELATED_ENTITY_TYPES } from "@/config/runOptions";
import { leadTypeLabel } from "@/config/crmOptions";

const NONE = "__none__";

const labelFor = (type, item) => {
  if (type === "contact") return `${item.first_name} ${item.last_name}`;
  if (type === "lead") return `${leadTypeLabel(item.lead_type)} · ${item.contact?.first_name || ""} ${item.contact?.last_name || ""}`.trim();
  if (type === "transaction") return item.property_address_line1;
  return "";
};

/** Reusable "link to a Contact / Lead / Transaction" picker for RUN dialogs. */
export const RelatedEntityPicker = ({ value, onChange, locked = false, allowedTypes = null }) => {
  const types = allowedTypes ? RELATED_ENTITY_TYPES.filter((t) => allowedTypes.includes(t.value)) : RELATED_ENTITY_TYPES;
  const [type, setType] = useState(value?.type || "");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => { setType(value?.type || ""); }, [value?.type]);

  useEffect(() => {
    if (locked || !type || value?.id || q.trim().length < 2) { setResults([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        let items = [];
        if (type === "contact") items = (await crmService.listContacts({ search: q.trim(), tab: "all" })).contacts;
        else if (type === "lead") items = (await crmService.listLeads({ search: q.trim() })).leads;
        else if (type === "transaction") items = (await crmService.listTransactions({ search: q.trim(), view: "all" })).transactions;
        if (active) setResults(items.slice(0, 6));
      } catch { /* ignore */ }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, type, value?.id, locked]);

  const pick = (item) => {
    onChange({ type, id: item.id, label: labelFor(type, item) });
    setResults([]); setQ("");
  };
  const clear = () => { onChange(null); setType(""); setQ(""); setResults([]); };

  if (value?.id) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2" data-testid="related-selected">
        <span className="truncate text-sm">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">{value.type}</span> · {value.label || value.id}
        </span>
        {!locked && <Button variant="ghost" size="sm" onClick={clear} data-testid="related-clear"><X className="h-4 w-4" /></Button>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Select value={type || NONE} onValueChange={(v) => { setType(v === NONE ? "" : v); setResults([]); }}>
        <SelectTrigger data-testid="related-type"><SelectValue placeholder="Not linked" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Not linked</SelectItem>
          {types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {type && (
        <>
          <div className="flex items-center gap-2 rounded-lg border border-border px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Find a ${type}…`} className="border-0 px-0 shadow-none focus-visible:ring-0" data-testid="related-search" />
          </div>
          {results.map((item) => (
            <button key={item.id} onClick={() => pick(item)} data-testid={`related-result-${item.id}`}
              className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-secondary">
              {labelFor(type, item)}
            </button>
          ))}
        </>
      )}
    </div>
  );
};
